import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getAnalytics } from 'firebase/analytics';

// Debug: Log environment loading
console.log('Environment check:', {
  mode: import.meta.env.MODE,
  dev: import.meta.env.DEV,
  prod: import.meta.env.PROD
});

// Debug: Log all VITE_ environment variables (without values)
const envVars = Object.keys(import.meta.env).filter(key => key.startsWith('VITE_'));
console.log('Available VITE_ environment variables:', envVars);

// Explicitly check each config value
const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID;
const appId = import.meta.env.VITE_FIREBASE_APP_ID;
const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;

// Debug: Log the raw value of VITE_FIREBASE_API_KEY
console.log('Raw API Key value type:', typeof import.meta.env.VITE_FIREBASE_API_KEY);
console.log('Is API Key undefined?', import.meta.env.VITE_FIREBASE_API_KEY === undefined);
console.log('Is API Key null?', import.meta.env.VITE_FIREBASE_API_KEY === null);
console.log('API Key length:', import.meta.env.VITE_FIREBASE_API_KEY?.length);

// Log presence and format of each value (safely)
console.log('Firebase config validation:', {
  apiKey: {
    present: !!apiKey,
    length: apiKey?.length,
    format: apiKey?.startsWith('AIza') ? 'valid prefix' : 'invalid prefix'
  },
  authDomain: {
    present: !!authDomain,
    endsWithFirebaseApp: authDomain?.endsWith('.firebaseapp.com')
  },
  projectId: {
    present: !!projectId
  },
  storageBucket: {
    present: !!storageBucket,
    endsWithAppspot: storageBucket?.endsWith('.appspot.com')
  },
  messagingSenderId: {
    present: !!messagingSenderId,
    isNumber: !isNaN(messagingSenderId)
  },
  appId: {
    present: !!appId,
    format: appId?.includes(':') ? 'contains separator' : 'missing separator'
  },
  measurementId: {
    present: !!measurementId,
    startsWithG: measurementId?.startsWith('G-')
  }
});

// Your Firebase configuration
const firebaseConfig = {
  apiKey,
  authDomain,
  projectId,
  storageBucket,
  messagingSenderId,
  appId,
  measurementId
};

// Debug: Log the config (safely)
console.log('Firebase config check:', {
  hasApiKey: !!apiKey,
  hasAuthDomain: !!authDomain,
  hasProjectId: !!projectId,
  hasStorageBucket: !!storageBucket,
  hasMessagingSenderId: !!messagingSenderId,
  hasAppId: !!appId,
  hasMeasurementId: !!measurementId
});

let app;
let auth;
let db;
let analytics;

try {
  if (!apiKey) {
    throw new Error('Firebase API key is missing. Check .env file and environment loading.');
  }

  console.log('Attempting Firebase initialization...');
  app = initializeApp(firebaseConfig);
  console.log('Firebase app initialized successfully');

  auth = getAuth(app);
  console.log('Firebase auth initialized');

  db = getFirestore(app);
  console.log('Firestore initialized');

  analytics = getAnalytics(app);
  console.log('Firebase analytics initialized');

  setPersistence(auth, browserSessionPersistence)
    .then(() => console.log('Auth persistence set'))
    .catch((error) => console.error("Error setting persistence:", error));

} catch (error) {
  console.error('Firebase initialization error:', error.message);
  console.error('Error details:', error);
  throw error;
}

export { auth, db, analytics };
export default app; 